import { LoaderFunctionArgs, useLoaderData } from "react-router"
import {
  DocsUiLink as Link,
  DocsRouterLink as RouterLink,
} from "~/docs-data/docs-link"
import { H1, H2, P, Section } from "~/components/Typography"
import { DocsBackLink } from "~/components/DocsBackLink"
import { MarkdownBody } from "~/components/MarkdownBody"
import { getMethodologyEntry } from "~/docs-data/methodology"
import { liveMethodologyEntries } from "~/docs-data/helpers"
import { getDocsCatalogForRequest } from "~/server/docs-catalog-store.server"

/**
 * A single technique article, the country-agnostic sibling of the per-country
 * methodology page. Only techniques applicable to a live (non-coming-soon)
 * market are reachable here; the rest 404 until their market is public.
 */
export const loader = async ({ params, request }: LoaderFunctionArgs) => {
  const entry = getMethodologyEntry(params.id ?? "")
  if (!entry) throw new Response("Not found", { status: 404 })
  const { countries } = await getDocsCatalogForRequest(request)
  const live = liveMethodologyEntries(countries)
  if (!live.some((e) => e.id === entry.id)) {
    throw new Response("Not found", { status: 404 })
  }
  return {
    entry,
    related: (entry.relatedTo ?? [])
      .map((id) => live.find((m) => m.id === id))
      .filter((m): m is NonNullable<typeof m> => Boolean(m)),
  }
}

export default function TechniquePage() {
  const { entry, related } = useLoaderData<typeof loader>()

  return (
    <div className="max-w-screen-xl">
      <Section>
        <DocsBackLink fallback="/docs/concepts/techniques" />
        <div className="flex items-center gap-1 text-sm text-muted-foreground mb-3">
          <Link to="/docs/concepts/techniques" variant="inline">
            Techniques
          </Link>
          <span>›</span>
          <span className="truncate">{entry.title}</span>
        </div>
        <H1>{entry.title}</H1>
        <P>{entry.longDescription}</P>
        {entry.body && <MarkdownBody source={entry.body} />}
      </Section>

      {related.length > 0 && (
        <Section>
          <H2>Related techniques</H2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {related.map((m) => (
              <RouterLink
                key={m.id}
                to={`/docs/concepts/techniques/${m.id}`}
                className="group flex flex-col rounded-lg border bg-card hover:border-primary hover:shadow-sm transition-all p-5"
              >
                <h3 className="font-semibold text-sm group-hover:text-primary transition-colors mb-2">
                  {m.title}
                </h3>
                <p className="text-xs text-muted-foreground line-clamp-2">
                  {m.shortDescription}
                </p>
              </RouterLink>
            ))}
          </div>
        </Section>
      )}
    </div>
  )
}
